import { useEffect, useRef, useState } from "react";
import { Search, AlertTriangle } from "lucide-react";
import api from "../lib/api";
import MemberDetail, { StatusPill, money } from "./MemberDetail";

const FILTERS = [["all", "All"], ["active", "Active"], ["notice", "On notice"], ["upcoming", "Starting soon"]];

export default function LocationMemberSearch({ location }) {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(null);
  const seq = useRef(0);

  useEffect(() => {
    const id = ++seq.current;
    const t = setTimeout(() => {
      api.get("/members", { params: { q, location, status } })
        .then(({ data }) => id === seq.current && setRows(data.members))
        .catch(() => id === seq.current && setRows([]));
    }, 200);
    return () => clearTimeout(t);
  }, [q, location, status]);

  const shown = (rows || []).slice(0, q ? 50 : 8);
  return (
    <div className="rounded-xl border border-brand-border bg-brand-surface p-5" data-testid="location-member-search-section">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-tertiary" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="location-member-search-input"
            placeholder="Search a person, company, email, phone or office #"
            className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-brand-page border border-brand-border text-sm text-brand-ink placeholder:text-brand-tertiary outline-none focus:border-brand-primary transition-colors" />
        </div>
        <div className="flex gap-1.5">
          {FILTERS.map(([k, l]) => (
            <button key={k} onClick={() => setStatus(k)} data-testid={`location-member-filter-${k}`}
              className={`rounded-full px-3 py-1.5 text-xs border transition-colors ${status === k ? "bg-brand-ink text-brand-page border-brand-ink" : "border-brand-border text-brand-muted hover:text-brand-ink"}`}>{l}</button>
          ))}
        </div>
      </div>
      <div className="mt-4 divide-y divide-brand-border" data-testid="location-member-results">
        {rows === null && [...Array(3)].map((_, i) => <div key={i} className="h-11 my-1 rounded bg-brand-hover animate-pulse" />)}
        {rows && shown.map((m) => (
          <button key={m.company_id} onClick={() => setOpen(m.company_id)} data-testid={`location-member-row-${m.company_id}`}
            className="w-full text-left py-2.5 px-2 -mx-2 rounded-lg flex flex-wrap items-center gap-x-4 gap-y-1 hover:bg-brand-hover transition-colors">
            <span className="min-w-0 flex-1">
              <span className="text-sm font-medium text-brand-ink flex items-center gap-1.5">{m.company_name}{m.billing?.delinquent && <AlertTriangle className="h-3.5 w-3.5 text-rose-600" />}</span>
              <span className="block text-xs text-brand-muted truncate">{m.contacts.map((c) => c.name).join(", ")}{m.contact_count > 3 ? ` +${m.contact_count - 3}` : ""}</span>
            </span>
            <span className="text-xs text-brand-ink w-28">{m.offices.map((o) => o.number).slice(0, 3).join(", ") || "Coworking"}</span>
            <span className="text-xs text-brand-ink w-20 text-right tabular-nums">{money(m.mrr)}</span>
            <span className="w-24 flex justify-end"><StatusPill status={m.status} /></span>
          </button>
        ))}
        {rows && !rows.length && <p className="py-4 text-sm text-brand-muted" data-testid="location-member-empty">No members match.</p>}
      </div>
      {rows && rows.length > shown.length && <p className="text-[11px] text-brand-tertiary mt-3">Showing {shown.length} of {rows.length} — type to narrow down.</p>}
      {open && <MemberDetail companyId={open} onClose={() => setOpen(null)} />}
    </div>
  );
}
